import {
  AccordionDetails,
  Avatar,
  Box,
  Divider,
  FormControl,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { IEquipmentItem } from 'API';
import { Dictionary } from 'Dictionary';
import { DICTIONARY } from 'Enum';
import { useIsDesktop } from 'Hooks';
import { PurpleWarning } from 'Images';
import { useState } from 'react';
import { illegalChars, lettersOrNumbersWithSomeExtraCharsMessage } from 'Regex';
import { IEquipmentOrderItem } from '..';

interface IDetails {
  handleCheckItem: (checked: boolean) => void;
  equipmentItem: IEquipmentOrderItem;
  item: IEquipmentItem;
  period: number;
  index: number;
}

export const Details = ({
  equipmentItem,
  item,
  period,
  index,
  handleCheckItem,
}: IDetails) => {
  const [error, setError] = useState<boolean>(false);
  const [quantityNoteCount, setQuantityNoteCount] = useState(0);
  const [orderNoteCount, setOrderNoteCount] = useState(0);

  const isDesktop = useIsDesktop();
  const dictionary = Dictionary[DICTIONARY.Equipment].createOrder;

  const MAX_LENGTH = 300;

  // TODO: FIX BUG -
  // There was a new requirement to set checkbox 'true' (if it is not already checked) when the user changes a quantity or writes some comment.
  // The following code solves this requirement but generates a bug that when the time period is changed to high period and then back to lower period, it marks checkboxes for no reason
  // useEffect(() => {
  //   handleCheckItem(
  //     equipmentItem.quantity > item.maxAVGQuantityPerMonth * (period || 1) ||
  //       orderNoteCount > 0
  //   );
  // }, [
  //   equipmentItem.quantity > item.maxAVGQuantityPerMonth * (period || 1),
  //   orderNoteCount > 0,
  // ]);

  return isDesktop ? (
    <AccordionDetails
      sx={{
        pt: 2,
        pb: 5,
        pr: 3,
        pl: 3,
      }}
    >
      <Divider
        aria-hidden='true'
        orientation='horizontal'
        variant='middle'
        flexItem
        sx={{
          height: '1px',
          width: '100%',
          alignSelf: 'center',
          borderColor: 'primary.shade_100',
          mb: '24px',
          top: '-10px',
          right: '15px',
          position: 'relative',
        }}
      />
      {equipmentItem.quantity &&
      equipmentItem.quantity > item.maxAVGQuantityPerMonth * (period || 1) ? (
        <Grid
          sx={{
            backgroundColor: 'primary.shade_100',
            borderRadius: '6px',
            p: '20px',
            mb: 3,
            '.MuiTextField-root': { background: 'transparent' },
          }}
        >
          <FormControl
            sx={{ width: '100%' }}
            onClick={(e: any) => e.stopPropagation()}
          >
            <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 2 }}>
              <Avatar
                sx={{
                  width: '20px',
                  height: '20px',
                  mr: 1,
                }}
                src={PurpleWarning}
                alt=''
              />

              <Typography
                component={'label'}
                htmlFor={`item-quantity-note-field-${index}`}
                variant='body2'
              >
                {dictionary.quantityNote}
              </Typography>
            </Box>

            <TextField
              id={`item-quantity-note-field-${index}`}
              name={`item-quantity-note-field-${index}`}
              placeholder={dictionary.quantityNotePlaceholder}
              multiline
              rows={2}
              sx={{
                backgroundColor: '#fff',
                width: '100%',
                '.MuiInputBase-formControl': {
                  borderRadius: '16px',
                  backgroundColor: '#fff',
                },
              }}
              onClick={(e) => e.stopPropagation()}
              onInput={(e: any) => {
                const newValue = e.target.value.replace(illegalChars, '');
                e.target.value =
                  newValue.length > MAX_LENGTH
                    ? newValue.substring(0, MAX_LENGTH)
                    : newValue;
                setQuantityNoteCount(e.target.value.length);
              }}
              inputProps={{
                'aria-label': dictionary.quantityNote,
                'aria-describedby': dictionary.insertUpTo100Chars,
              }}
            />
            <Typography
              sx={{
                fontSize: '14px',
              }}
            >
              {quantityNoteCount}/{MAX_LENGTH}
            </Typography>
          </FormControl>
        </Grid>
      ) : (
        <FormControl sx={{ width: '100%' }}>
          <Typography
            component={'label'}
            htmlFor={`item-note-field-${index}`}
            fontWeight={700}
            sx={{ pl: '20px' }}
            onClick={(e: any) => e.stopPropagation()}
          >
            {dictionary.addOrderComments}
          </Typography>
          <TextField
            id={`item-note-field-${index}`}
            name={`item-note-field-${index}`}
            placeholder={dictionary.orderNotePlaceholder}
            multiline
            rows={3}
            sx={{
              backgroundColor: '#fff',
              mb: '20px',
              width: '100%',
              p: '10px 20px 10px 20px',
              '.MuiInputBase-formControl': {
                borderRadius: '6px',
                backgroundColor: '#fff',
              },
            }}
            onClick={(e) => e.stopPropagation()}
            error={error}
            onInput={(e: any) => {
              const newValue = e.target.value.replace(illegalChars, '');
              e.target.value =
                newValue.length > MAX_LENGTH
                  ? newValue.substring(0, MAX_LENGTH)
                  : newValue;
              setOrderNoteCount(e.target.value.length);
            }}
            helperText={error ? lettersOrNumbersWithSomeExtraCharsMessage : ''}
            inputProps={{
              'aria-label': dictionary.addOrderComments,
              'aria-describedby': dictionary.insertUpTo100Chars,
            }}
          />
          <Typography
            sx={{
              fontSize: '16px !important',
              position: 'relative',
              top: '-60px',
              ml: 'auto',
              p: '0 35px',
              color: 'purple2.main',
            }}
            variant='body7'
          >
            {orderNoteCount}/{MAX_LENGTH}
          </Typography>
        </FormControl>
      )}
    </AccordionDetails>
  ) : (
    // MOBILE UI
    <AccordionDetails
      sx={{
        pt: 3,
        pb: 5,
        pr: 2,
        pl: 2,
      }}
    >
      <Divider
        aria-hidden='true'
        orientation='horizontal'
        variant='middle'
        flexItem
        sx={{
          height: '1px',
          width: '100%',
          alignSelf: 'center',
          borderColor: 'primary.shade_100',
          mb: '24px',
          top: '-10px',
          right: '15px',
          position: 'relative',
        }}
      />
      {equipmentItem.quantity &&
      equipmentItem.quantity > item.maxAVGQuantityPerMonth * (period || 1) ? (
        <Grid
          sx={{
            backgroundColor: 'primary.shade_100',
            borderRadius: '6px',
            p: '20px',
            mb: 3,
            '.MuiTextField-root': { background: 'transparent' },
          }}
        >
          <Box sx={{ display: 'inline-flex', alignItems: 'center', mb: 2 }}>
            <Avatar
              sx={{
                width: '20px',
                height: '20px',
                mr: 1,
                alignSelf: 'flex-start',
              }}
              src={PurpleWarning}
              alt=''
            />
            <Typography variant='body2'>{dictionary.quantityNote}</Typography>
          </Box>

          <TextField
            id={`item-quantity-note-field-${index}`}
            name={`item-quantity-note-field-${index}`}
            placeholder={dictionary.quantityNotePlaceholder}
            multiline
            rows={3}
            sx={{
              backgroundColor: '#fff',
              width: '100%',
              '.MuiInputBase-formControl': {
                borderRadius: '16px',
                backgroundColor: '#fff',
                fontSize: isDesktop ? '16px' : '14px',
              },
            }}
            onClick={(e) => e.stopPropagation()}
            onInput={(e: any) => {
              const newValue = e.target.value.replace(illegalChars, '');
              e.target.value =
                newValue.length > MAX_LENGTH
                  ? newValue.substring(0, MAX_LENGTH)
                  : newValue;
              setQuantityNoteCount(e.target.value.length);
            }}
            helperText={error ? lettersOrNumbersWithSomeExtraCharsMessage : ''}
            error={error}
          />
          <Typography
            sx={{
              fontSize: '14px',
            }}
          >
            {quantityNoteCount}/{MAX_LENGTH}
          </Typography>
        </Grid>
      ) : (
        <>
          <Typography fontWeight={700} sx={{ pl: '20px' }}>
            {dictionary.addOrderComments}
          </Typography>
          <TextField
            id={`item-note-field-${index}`}
            name={`item-note-field-${index}`}
            placeholder={dictionary.orderNotePlaceholder}
            multiline
            rows={3}
            sx={{
              backgroundColor: '#fff',
              mb: '20px',
              width: '100%',
              p: '10px 10px 15px 10px',
              '.MuiInputBase-formControl': {
                borderRadius: '6px',
                fontSize: '16px',
              },
            }}
            onClick={(e) => e.stopPropagation()}
            error={error}
            onInput={(e: any) => {
              const newValue = e.target.value.replace(illegalChars, '');
              e.target.value =
                newValue.length > MAX_LENGTH
                  ? newValue.substring(0, MAX_LENGTH)
                  : newValue;
              setOrderNoteCount(e.target.value.length);
            }}
            helperText={error ? lettersOrNumbersWithSomeExtraCharsMessage : ''}
          />
          <Typography
            sx={{
              fontSize: '16px !important',
              position: 'relative',
              top: '-60px',
              p: '0 25px',
              color: 'purple2.main',
              width: '100%',
              display: 'block',
              textAlign: 'right',
            }}
            variant='body7'
          >
            {orderNoteCount}/{MAX_LENGTH}
          </Typography>
        </>
      )}
    </AccordionDetails>
  );
};
